import { usePlanner } from "../contexts/PlannerContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, DropdownMenuPortal } from "@/components/ui/dropdown-menu";
import { Check, ListFilter, Loader2, X } from "lucide-react";
import { memo, useCallback, useState, useMemo } from "react";
import { useCourses } from "@/lib/courses";
import { addDays } from "date-fns";
import { useAllAssignmentTypes } from "@/lib/useAllAssignmentTypes";


export const statusLabels: any = {
    "incomplete": "Not started",
    "in-progress": "In progress",
    "complete": "Complete",
    "graded": "Graded"
}

export const durationLabels: any = {
    "short": "Short (< 30m)",
    "medium": "Medium (30m - 1hr)",
    "long": "Long (1hr - 2hr)",
    "extended": "Extended (> 2hr)"
}

export const dueLabels: any = {
    "overdue": "Overdue",
    "today": "Today",
    "tomorrow": "Tomorrow"
}

export const linkedLabels: any = {
    "haslinked": "Has linked items",
    "nolinked": "No linked items",
}

const HeaderFilterItem = memo(({ value, onClick }: { value: any, onClick: () => void }) => {

    return (
        <button className="flex items-center gap-[5px] text-sm bg-theme-1 rounded-full px-[10px] py-[2px] cursor-pointer hover:bg-theme-2" onClick={onClick}>
            <p>{value}</p>
            <X className="h-[14px] w-[14px] text-theme-0" />
        </button>
    )
})

const filterLabels: any = {
    "course": "Course:",
    "status": "Status:",
    "duration": "Estimated time:",
    "due": "Due date:",
    "type": "Type:",
}

export const HeaderActiveFilters = () => {

    const { filters, updateFilters } = usePlanner();
    const { courseData } = useCourses();
    const { typeLabels } = useAllAssignmentTypes();

    const labelLookup: any = {
        "status": statusLabels,
        "duration": durationLabels,
        "type": typeLabels,
        "linked": linkedLabels,
    }

    const getTypeLabel = (value: string) => {
        if (value.includes(':')) {
            const [courseId, typeName] = value.split(':');
            const course = courseData?.[courseId];
            const courseName = course?.code || course?.default || '';
            const typeLabel = typeLabels[value] || typeName;
            return `${courseName}: ${typeLabel}`;
        }
        return typeLabels[value] || value;
    };

    const handleRemove = useCallback((filterName: string, filterValue: any) => {
        updateFilters({ [filterName]: filters[filterName].filter((value: any) => value !== filterValue) });
    }, [filters, updateFilters]);

    const handleClearAll = useCallback(() => {
        updateFilters({
            course: [],
            status: [],
            duration: [],
            type: [],
            linked: [],
        });
    }, [updateFilters]);

    const render = () => {

        let output: any[] = [];
        Object.keys(filters).map((key) => {
            filters[key].forEach((value: any) => {
                let label;
                if (key === "course") {
                    label = courseData?.[value]?.code !== "" ? courseData?.[value]?.code : courseData?.[value]?.default;
                } else if (key === "type") {
                    label = getTypeLabel(value);
                } else {
                    label = labelLookup[key]?.[value];
                }
                output.push(
                    <HeaderFilterItem key={key + "_" + value} value={label} onClick={() => handleRemove(key, value)} />
                )
            });
        });

        return output;
    }

    const items = render();

    if (items.length === 0) return null;

    return (
        <div className="flex items-center gap-[5px] px-[10px] pb-[10px]">
            {items}
            <button
                className="text-sm text-theme-0/60 cursor-pointer hover:text-theme-0 ml-[5px] underline"
                onClick={handleClearAll}
            >
                Clear Filters
            </button>
        </div>
    )
}


const FilterItem = memo(({ label, checked, onClick }: { label: string, checked: boolean, onClick: () => void }) => {
    return (
        <div className="flex items-center gap-[5px] cursor-pointer" onClick={onClick}>
            <Check className="h-[16px] w-[16px] transition-opacity duration-200" style={{ "opacity": checked ? 1 : 0 }} />
            <label htmlFor={label}>{label}</label>
        </div>
    )
})


const CourseFilter = () => {

    const { updateFilters, filters } = usePlanner();

    const { courseData } = useCourses();

    const onClick = (course: string) => {
        updateFilters({ "course": addOrRemoveFilter(filters["course"], course) });
    }

    const render = () => {
        return Object.keys(courseData).map((id: any) => {
            const details = courseData[id];
            return (
                <FilterItem
                    key={id}
                    label={details.default}
                    onClick={() => onClick(id)}
                    checked={filters["course"].includes(id)}
                />
            )
        })
    }


    return render();
}

const StatusFilter = () => {
    const { updateFilters, filters } = usePlanner();

    const render = () => {

        return Object.keys(statusLabels).map((status) => {
            return (
                <FilterItem
                    key={status}
                    label={statusLabels[status]}
                    onClick={() => { updateFilters({ "status": addOrRemoveFilter(filters["status"], status) }); }}
                    checked={filters["status"].includes(status)}
                />
            )
        })
    }


    return render();
}

const DurationFilter = () => {

    const { updateFilters, filters } = usePlanner();

    const render = () => {

        return Object.keys(durationLabels).map((item) => {
            return (
                <FilterItem
                    key={item}
                    label={durationLabels[item]}
                    onClick={() => { updateFilters({ "duration": addOrRemoveFilter(filters["duration"], item) }); }}
                    checked={filters["duration"].includes(item)}
                />
            )
        })
    }

    return render();
}

const DropDownSub = ({ children, open, setOpen, close }: { children: React.ReactNode[], open: boolean, setOpen: (open: boolean) => void, close: () => void }) => {

    const handlePointerEnter = () => {
        close();
        setOpen(true);
    }

    const { portal } = usePlanner();

    return (
        <DropdownMenuSub open={open}>
            <DropdownMenuSubTrigger onPointerEnter={handlePointerEnter}>
                {children?.[0]}
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal container={portal}>
                <DropdownMenuSubContent className="flex flex-col gap-[5px]">
                    {children?.[1]}
                </DropdownMenuSubContent>
            </DropdownMenuPortal>
        </DropdownMenuSub>
    )
}

const DueFilter = () => {

    const { updateFilters, filters } = usePlanner();

    const render = () => {
        const today = new Date();
        const tomorrow = addDays(today, 1);
        return [
            { "label": "Overdue", "value": { "label": "overdue", "date": today } },
            { "label": "Today", "value": { "label": "today", "date": today } },
            { "label": "Tomorrow", "value": { "label": "tomorrow", "date": tomorrow } },
        ].map((item) => {
            return (
                <FilterItem
                    key={item.label}
                    label={item.label}
                    onClick={() => { updateFilters({ "due": addOrRemoveFilter(filters["due"], item.value) }); }}
                    checked={filters["due"].includes(item.value.label)}
                />
            )
        })
    }

    return render();
}

const TypeFilter = () => {

    const { updateFilters, filters } = usePlanner();
    const { typesByCourse, isLoading, error } = useAllAssignmentTypes();

    if (isLoading) {
        return (
            <div className="flex items-center gap-2 text-theme-1 py-2 px-1">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-sm text-red-500 py-2 px-1">{error}</div>
        );
    }

    if (typesByCourse.length === 0) {
        return (
            <div className="text-sm text-theme-1 py-2 px-1">No types found</div>
        );
    }

    return (
        <div className="max-h-[300px] overflow-y-auto">
            {typesByCourse.map((group, index) => (
                <div key={group.courseId}>
                    {index > 0 && <div className="h-px bg-theme-1/20 my-2" />}
                    <div className="text-xs font-medium text-theme-1 px-1 py-1 truncate">
                        {group.courseName}
                    </div>
                    {group.types.map((type) => (
                        <FilterItem
                            key={`${group.courseId}-${type.value}`}
                            label={type.label}
                            onClick={() => { updateFilters({ "type": addOrRemoveFilter(filters["type"], type.value) }); }}
                            checked={filters["type"].includes(type.value)}
                        />
                    ))}
                </div>
            ))}
        </div>
    );
}

const LinkedFilter = () => {

    const { updateFilters, filters } = usePlanner();

    return (

        Object.keys(linkedLabels).map((item) => {
            return (
                <FilterItem
                    key={item}
                    label={linkedLabels[item]}
                    onClick={() => { updateFilters({ "linked": addOrRemoveFilter(filters["linked"], item) }); }}
                    checked={filters["linked"].includes(item)}
                />
            )
        })

    )
}


const addOrRemoveFilter = (values: any[], value: any) => {

    if (values.includes(value)) {
        return values.filter((v: any) => v !== value);
    }

    return [...values, value];

}


const PlannerHeaderFilter = () => {

    const { updateFilters, filters, portal } = usePlanner();


    const [courseOpen, setCourseOpen] = useState(false);
    const [statusOpen, setStatusOpen] = useState(false);
    const [durationOpen, setDurationOpen] = useState(false);
    const [typeOpen, setTypeOpen] = useState(false);
    const [linkedOpen, setLinkedOpen] = useState(false);

    const closeAll = () => {
        setCourseOpen(false);
        setStatusOpen(false);
        setDurationOpen(false);
        setTypeOpen(false);
        setLinkedOpen(false);
    }

    return (
        <>
            <DropdownMenu modal={false} onOpenChange={closeAll}>
                <DropdownMenuTrigger asChild>
                    <button className="text-sm flex items-center gap-[5px] px-[10px] rounded-lg py-1 text-theme-0 border border-theme cursor-pointer hover:bg-theme-1 transition-all duration-200">
                        <ListFilter className="h-[16px] w-[16px]" />
                        <p>Filter</p>
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>

                    <DropDownSub open={courseOpen} setOpen={setCourseOpen} close={closeAll}>
                        <p>Course</p>
                        <CourseFilter />
                    </DropDownSub>
                    <DropDownSub open={statusOpen} setOpen={setStatusOpen} close={closeAll}>
                        <p>Status</p>
                        <StatusFilter />
                    </DropDownSub>
                    <DropDownSub open={durationOpen} setOpen={setDurationOpen} close={closeAll}>
                        <p>Time estimate</p>
                        <DurationFilter />
                    </DropDownSub>
                    <DropDownSub open={typeOpen} setOpen={setTypeOpen} close={closeAll}>
                        <p>Type</p>
                        <TypeFilter />
                    </DropDownSub>
                    <DropDownSub open={linkedOpen} setOpen={setLinkedOpen} close={closeAll}>
                        <p>Linked items</p>
                        <LinkedFilter />
                    </DropDownSub>
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    )
}

export default PlannerHeaderFilter;